import * as React from 'react'
import { View, Text } from 'react-native'
import styled from '@theme/styled-components'

import TextInput from '@components/textInput/TextInput'
import Choice from '@components/choice/Choice'
import { Animation } from '@components/root/Animators'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import * as LocationStore from '@store/location/location'
import * as SiteStore from '@store/site/site'
import * as IncidentSore from '@store/incident/incident'

import { FormikHelpers, useFormik } from 'formik'

import sanitize from '@utils/Sanitizer'

interface Values {
  space: string
}

const valuesToReset: (keyof IncidentForm)[] = ['location', 'subLocation', 'subsubLocation', 'plan']

const Position = ({ form, onForm }: IncidentFormProps) => {
  const init: Values = React.useMemo(() => ({ space: form.space }), [form.space])

  const i18n = useI18n()
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations.inProgress)

  const locations = useReducer(LocationStore.store, (s) => s.places)
  const spaces = React.useMemo(() => locations.filter((l) => l.enabled), [locations])

  const manifestation = React.useMemo(
    () => manifestations.find((m) => m.id === form.manifestationId),
    [manifestations, form.manifestationId]
  )

  const [search, setSearch] = React.useState('')

  const places = React.useMemo(
    () => spaces.filter((s) => !manifestation || manifestation.espaceIds.includes(s.id)),
    [spaces, manifestation]
  )

  const buildings = React.useMemo(
    () => places.filter((p) => !p.parentId).filter((p) => sanitize(p.libelle).includes(sanitize(search))),
    [places, search]
  )

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    const resetValues =
      vals.space !== form.space
        ? {
            ...valuesToReset.reduce(
              (acc, cur) => ({ ...acc, [cur]: IncidentSore.initialState.form[cur] }),
              {} as Partial<IncidentForm>
            ),
            completed: false,
          }
        : {}

    onForm({ ...vals, ...resetValues }, true).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  const options = React.useMemo(
    () => buildings.map((b) => ({ label: b.libelle, value: b.id })).sort((a, b) => a.label.localeCompare(b.label)),
    [buildings]
  )
  const opt = options.find((o) => values.space === o.value)

  return (
    <MainContainer>
      <Animation>
        <SearchContainer>
          <TextInput
            value={search}
            onChange={setSearch}
            placeholder={i18n.t('screens.incident.POSITION.search')}
            left={{ name: 'search' }}
          />
        </SearchContainer>
      </Animation>

      {options.length > 0 ? (
        <Choice
          data={options}
          onItemSelected={(next) => setFieldValue('space', next.value)}
          value={opt}
          inline={false}
        />
      ) : (
        <MissingInfo>{i18n.t('screens.incident.POSITION.noPosition')}</MissingInfo>
      )}
    </MainContainer>
  )
}

export default Position

const MainContainer = styled(View)`
  margin: 0 20px;
  flex: 1;
`

const SearchContainer = styled(View)`
  margin-bottom: 25px;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`
